import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { Textarea } from '@workspace/ui/components/textarea'
import { Mail } from 'lucide-react'
import { useEffect, useState } from 'react'

/** Another wording for the same request, for a surface that says it differently. */
export interface ConnectDialogTexts {
  title: string
  description: string
  label: string
  tip: string
  cancel: string
  send: string
}

interface ConnectDialogProps {
  startupName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the user's message when they submit the request. */
  onConnect: (message: string) => void
  isPending?: boolean
  texts?: ConnectDialogTexts
}

function defaultMessage(startupName: string) {
  const firstWord = startupName.split(' ')[0] || startupName
  return `Hi ${firstWord} team,\n\nI saw your profile and what you're building resonates with my thesis. Would love to learn more, and happy to take a 30-min intro call.\n\nBest,`
}

export function ConnectDialog({
  startupName,
  open,
  onOpenChange,
  onConnect,
  isPending,
  texts,
}: ConnectDialogProps) {
  const [message, setMessage] = useState(() => defaultMessage(startupName))

  useEffect(() => {
    if (open) setMessage(defaultMessage(startupName))
  }, [open, startupName])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-emerald-400/40 bg-emerald-400/10">
              <Mail className="size-5 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Request an intro
              </p>
              <AlertDialogTitle className="truncate text-xl leading-tight">
                {texts?.title ?? `Connect with ${startupName}`}
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
            {texts?.description ??
              "We'll forward your note to the founders together with your investor profile. They'll reply directly to your email."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            {texts?.label ?? 'Your message'}
          </p>
          <Textarea
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Why you'd like to talk, what you can offer, any specific questions…"
            className="resize-none"
          />
          <p className="text-[10px] text-muted-foreground/70">
            {texts?.tip ??
              'Tip: a single concrete thing you loved about the deck gets 3× more replies than a generic intro.'}
          </p>
        </div>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel className="sm:mr-auto">{texts?.cancel ?? 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending || !message.trim()}
            onClick={() => onConnect(message.trim())}
            className="border-emerald-500/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200"
          >
            {isPending ? 'Sending...' : (texts?.send ?? 'Send introduction request')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
